/** League / division helpers — CSV mapping and team_summary use AL/NL plus division labels. */

export type LeagueFilter = 'all' | 'AL' | 'NL'
export type DivisionFilter = 'all' | 'East' | 'Central' | 'West'

export function leagueCodeFromField(League: unknown): 'AL' | 'NL' | null {
  const raw = String(League ?? '').trim()
  const u = raw.toUpperCase()
  if (u === 'AL' || u.startsWith('AMERICAN')) return 'AL'
  if (u === 'NL' || u.startsWith('NATIONAL')) return 'NL'
  return null
}

/** Last token of division string, e.g. "AL East" → "East" (matches simulator normalization). */
export function divisionTokenFromField(Division: unknown): string {
  const parts = String(Division ?? '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (!parts.length) return ''
  return parts[parts.length - 1]!
}

export interface TeamRegion {
  league: 'AL' | 'NL' | null
  div: string
}

export function buildTeamRegionMap(rows: ReadonlyArray<Record<string, unknown>>): Map<string, TeamRegion> {
  const m = new Map<string, TeamRegion>()
  for (const r of rows) {
    const name = String(r.Mapping_Name ?? r.Team ?? '').trim()
    if (!name) continue
    m.set(name, {
      league: leagueCodeFromField(r.League),
      div: divisionTokenFromField(r.Division),
    })
  }
  return m
}

export function teamPassesRegionFilters(
  team: string,
  meta: Map<string, TeamRegion>,
  league: LeagueFilter,
  division: DivisionFilter,
): boolean {
  if (meta.size === 0) return true
  const noRegionFilter = league === 'all' && division === 'all'
  const row = meta.get(team)
  if (!row) return noRegionFilter

  if (league !== 'all') {
    if (!row.league || row.league !== league) return false
  }
  if (division !== 'all' && row.div !== division) return false
  return true
}

export function standingsRowPassesFilters(
  row: { League: string; Division: string },
  league: LeagueFilter,
  division: DivisionFilter,
): boolean {
  if (league !== 'all' && row.League !== league) return false
  if (division !== 'all' && divisionTokenFromField(row.Division) !== division) return false
  return true
}
